import { handleUnknownError } from '@news-mono/common'
import {
    DataLayerEventName,
    Product,
    scaleImage,
    VideoDetails,
} from '@news-mono/web-common'
import SwmPlayer from '@vpp/swm-player'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'
import cuid from 'cuid'
import React from 'react'
import { adBlockEnabled } from '../../../advertising/gpt/utils'
import { RealPlayerStateAdapter } from '../../../content/Video/providers/PlayerState'
import { StyledBrightcoveVideo } from '../../../content/Video/providers/SwmPlayerProvider.styled'
import { getCustParams } from '../../../content/Video/utils/get-ima-options'
import {
    EventsHandler,
    partialPlayerEvent,
} from '../../../content/Video/utils/handle-events'
import { sanitiseVideoId } from '../../../content/Video/utils/sanitise-video-id'
import { isMobile } from '../utils/can-autoplay'
import { isMobileViewport } from '../utils/is-mobile-viewport'
import { getPosterImage } from '../utils/poster'
import { getImageWidth, getNielsenProgramName } from '../utils/product-info'
import { swmPlayerDebug } from './debug-log'
import { PlayerProps } from './PlayerProps'

let videoPlayersCount = 0

interface State {
    errorEncountered: undefined | Error
}

function scaledPosterSrc(
    currentVideo: VideoDetails,
    product: Product,
    currentVideoMetadata: VideoMetaDTO,
) {
    const poster = getPosterImage(currentVideo)
    const imgWidth = getImageWidth(product)
    let posterSrcScaled: string | undefined

    if (poster) {
        if (poster.reference) {
            posterSrcScaled = scaleImage(poster.reference, imgWidth, false)
        } else if (currentVideoMetadata.posterImage) {
            posterSrcScaled = scaleImage(
                currentVideoMetadata.posterImage.reference,
                imgWidth,
                false,
            )
        }
    }

    return posterSrcScaled
}

export default class SwmPlayerProvider extends React.Component<
    PlayerProps,
    State
> {
    static displayName = 'SwmPlayerProvider'
    static swmPlayer?: SwmPlayer

    playerInstance: Videojs.PlayerInstance | null = null
    playerInstanceEventsHandler: EventsHandler | null = null
    playerInstanceRef = React.createRef<HTMLVideoElement>()
    videoElementId: string
    mounted = false

    constructor(props: PlayerProps) {
        super(props)

        this.state = {
            errorEncountered: undefined,
        }

        /**
         * OPS-931: the ID of the HTML video element must start with a letter.
         * In IE11 with Flash enabled, this video element ID is copied onto a
         * Flash object element. The object element must have an ID that begins
         * with a letter, otherwise IE11 will not be able to locate it.
         */
        this.videoElementId = sanitiseVideoId(
            `bc-video-${props.currentVideo.id}-${videoPlayersCount++}`,
        )

        // In the client
        if (typeof document !== 'undefined' && !SwmPlayerProvider.swmPlayer) {
            // TODO: We still aren't sure what to do with these topics, because for display ads
            // we switched to the names of topics instead of their IDs, but in Brightcove
            // we only have the IDs

            const custPrams = getCustParams({
                noCustParamsLabel: true,
                adUnitPath: props.playerOptions.adUnitPath,
            })
            interface swmPlayerParamsInterface {
                IMA3_REQUEST_MODE: BrightcovePlayer.AdRequestMode | undefined
                DFP_CUST_PARAMS: string
                IMA3_TIMEOUT: number
                IMA3_VPAID_MODE: string
                IMA3_SHOW_VPAID_CONTROLS: boolean
                IMA3_DEBUG: boolean
                DISABLE_POSTCODE_FETCH: boolean
                DISABLE_SNOWPLOW: boolean
                DISABLE_PREBID: boolean
                PARSELY_API_KEY: string | undefined
                PREBID_DEBUG: boolean
                PREBID_APPNEXUS_PLACEMENT_ID: number
                PREBID_IX_SITE_ID: number
                IMA3_AD_TECH_ORDER: ['html5']
                SNOWPLOW_APP_ID: string
                SNOWPLOW_APP_NAME: string
                INTEGRAL_AN_ID: number
                INTEGRAL_CHAN_ID: string
                NIELSEN_API_KEY: string
                NIELSEN_PROGRAM_NAME:
                    | '7NEWS Videos'
                    | 'PerthNow Videos'
                    | 'The West Videos'
                    | 'The Nightly Videos'
                NIELSEN_SUB_BRAND_ID?:
                    | 'c28'
                    | 'c29'
                    | 'c30'
                    | 'c31'
                    | 'c32'
                    | 'c33'
                GDPR_MODE: string | undefined // The typing here needs fixing on swmplayer
            }

            const swmPlayerParams: swmPlayerParamsInterface = {
                IMA3_REQUEST_MODE:
                    props.playerOptions.adRequestMode || 'onplay',
                DFP_CUST_PARAMS: custPrams,
                IMA3_TIMEOUT: 10000,
                IMA3_VPAID_MODE: 'ENABLED',
                IMA3_SHOW_VPAID_CONTROLS: true,
                IMA3_DEBUG: false,
                DISABLE_POSTCODE_FETCH: true,
                DISABLE_SNOWPLOW: !props.playerOptions.enableSnowplow,
                DISABLE_PREBID: !props.playerOptions.enablePrebid,
                PARSELY_API_KEY: this.props.config.parselyApikey,
                PREBID_DEBUG: this.props.config.prebidDebug,
                PREBID_APPNEXUS_PLACEMENT_ID:
                    this.props.config.prebidAppNexusId,
                PREBID_IX_SITE_ID: this.props.config.prebidIxId,
                // TODO: don't hardcode this - we should refactor config
                IMA3_AD_TECH_ORDER: ['html5'],
                SNOWPLOW_APP_ID: this.props.config.snowPlowAppId,
                SNOWPLOW_APP_NAME: this.props.config.snowPlowAppName,
                INTEGRAL_AN_ID: this.props.config.iasClientId,
                INTEGRAL_CHAN_ID: this.props.playerOptions.adUnitPath,
                NIELSEN_API_KEY: this.props.config.nielsenApid,
                NIELSEN_PROGRAM_NAME: getNielsenProgramName(this.props.product),
                GDPR_MODE: this.props.playerOptions.gdprMode as any,
                // The typing here needs fixing on swmplayer, usage is if (this._config.GDPR_MODE) so a boolean is fine
            }

            swmPlayerDebug('swmPlayerParams', swmPlayerParams)
            SwmPlayerProvider.swmPlayer = new SwmPlayer(swmPlayerParams)

            const Snowplow = require('@vpp/snowplow').default
            const Integral = require('@vpp/integral').default
            const Nielsen = require('@vpp/nielsen').default

            SwmPlayerProvider.swmPlayer.plugins([
                Snowplow,
                Integral,
                Nielsen,
                ...(this.props.playerOptions.isMagniteEnabled
                    ? []
                    : [require('@vpp/prebid').default]),
            ])
            SwmPlayerProvider.swmPlayer.init(false)
        }
    }

    get isMobileAutoPlayEnabled() {
        const { product, isNgnPerthNow, autoplayOptions } = this.props

        const isSevenNews = product === Product.SevenNews
        const isPerthNow = product === Product.PerthNow && isNgnPerthNow

        const canAutoplayOnMobile =
            (isSevenNews || isPerthNow) && isMobileViewport()

        return canAutoplayOnMobile && autoplayOptions ? true : false
    }

    getOffsetWidth() {
        if (!this.playerInstance) {
            return
        }
        return this.playerInstance.el_.offsetWidth
    }

    componentDidMount() {
        this.mounted = true
        if (this.playerInstanceRef.current) {
            this.mountVideoPlayer(this.playerInstanceRef.current)
        }
    }

    componentWillUnmount() {
        try {
            this.mounted = false
            if (this.playerInstanceEventsHandler) {
                this.playerInstanceEventsHandler.dispose()
                this.playerInstanceEventsHandler = null
            }
            if (this.playerInstance) {
                this.playerInstance.off('mouseover', this.playerOnMouseOver)
                this.playerInstance.off('loadedmetadata', this.enforceMuteState)
                this.playerInstance.off('ima3-started', this.enforceMuteState)
                this.playerInstance.off('playing', this.enforceMuteState)

                // the ad player volume does not reset upon ad play on iOS P19-688
                if (isMobile.iOS()) {
                    this.playerInstance.off(
                        'ima3-started',
                        this.resetAdPlayerVolume,
                    )
                }
                const playerInstance = this.playerInstance
                setTimeout(() => playerInstance.dispose())
                this.playerInstance = null
            }
        } catch (error) {
            const err = handleUnknownError(error)
            this.props.logger.error(
                { err },
                'Error unmounting video component, player is null',
            )
        }
    }

    componentDidUpdate(prevProps: PlayerProps) {
        if (this.playerInstance === null || this.playerInstance === undefined) {
            return
        }

        if (prevProps.currentVideo.id !== this.props.currentVideo.id) {
            this.updateVideo()
        }

        if (!prevProps.play && this.props.play) {
            const playerInstance = this.playerInstance
            const res = this.playerInstance.play()

            swmPlayerDebug('play() response', res)
            swmPlayerDebug('playerInstance.el_', playerInstance.el_)

            if (res) {
                res.catch((err) => {
                    swmPlayerDebug('Play state change failed', err)

                    this.props.onEvent({
                        type: DataLayerEventName.playToPauseError,
                        ...partialPlayerEvent(),
                        payload: {
                            muted: this.playerInstance!.muted(),
                            volume: this.playerInstance!.volume(),
                            paused: this.playerInstance!.paused(),
                        },
                    })
                })
            }
        }

        // Always sync mute state when it changes, or after video change, or when ad state changes
        // This ensures mute state is always in sync with React state regardless of which player is active
        if (
            prevProps.isMuted !== this.props.isMuted ||
            prevProps.adPlaying !== this.props.adPlaying
        ) {
            this.enforceMuteState()
        }

        if (prevProps.pause !== this.props.pause) {
            if (this.props.pause === true) {
                if (this.props.adPlaying) {
                    this.playerInstance.ima3.adPlayer.pause()
                } else {
                    ;(this.playerInstance as any).pause()
                }
            } else {
                if (this.props.adPlaying) {
                    this.playerInstance.ima3.adPlayer.play()
                } else {
                    ;(this.playerInstance as any).play()
                }
            }
        }
    }

    enforceMuteState = () => {
        if (!this.mounted || !this.playerInstance) {
            return
        }
        if (this.props.adPlaying && this.playerInstance.ima3?.adPlayer) {
            this.playerInstance.ima3.adPlayer.muted(this.props.isMuted)
        } else {
            this.playerInstance.muted(this.props.isMuted)
        }
    }

    updateVideo = async () => {
        try {
            if (
                this.playerInstance === null ||
                this.playerInstanceEventsHandler === null ||
                !this.playerInstanceRef.current
            ) {
                return
            }
            const { currentVideo, currentVideoMetadata, product } = this.props

            if (
                currentVideo === undefined ||
                currentVideoMetadata === undefined ||
                !SwmPlayerProvider.swmPlayer
            ) {
                return
            }

            const videoMetadata =
                await SwmPlayerProvider.swmPlayer.fetchMetadata(
                    this.playerInstanceRef.current.id,
                    {
                        videoId: currentVideoMetadata.id,
                        accountId: currentVideoMetadata.accountId,
                    },
                )
            if (!this.mounted) {
                return
            }
            // Note that our metadata object is not in the shape that swmPlayer can properlly work with, hence why we must
            // load the video metadata again using "fetchMetadata". In the future, we should load the metadata object
            // using swmPlayers api, instead of our own.
            SwmPlayerProvider.swmPlayer.loadFromMetadata(
                this.playerInstanceRef.current.id,
                videoMetadata,
            )

            if (currentVideoMetadata.posterImage) {
                const poster = scaledPosterSrc(
                    currentVideo,
                    product,
                    currentVideoMetadata,
                )

                this.playerInstance.poster(
                    poster ?? currentVideoMetadata.posterImage.reference,
                )
            }

            // Sync mute state immediately after loadFromMetadata
            this.enforceMuteState()

            this.playerInstanceEventsHandler.videoChanged()
        } catch (error) {
            const err = handleUnknownError(error)
            this.setState({ errorEncountered: err })
            this.props.logger.error({ err }, 'SwmPlayer update video error')
        }
    }

    mountVideoPlayer = async (videoElement: HTMLVideoElement) => {
        try {
            if (
                !this.playerInstanceRef ||
                !SwmPlayerProvider.swmPlayer ||
                this.props.currentVideo === undefined ||
                this.props.currentVideoMetadata === undefined
            ) {
                return
            }

            // disableNielsen here does it per player, not globally like other config is set further up
            // instance name gives each swm player a unique instance name
            await SwmPlayerProvider.swmPlayer.initPlayer(videoElement, {
                // override live streams to deliver client-side ads only
                // as WAN Brightcove account does not support live config for SSAI
                adChoice:
                    this.props.currentVideo.videoType === 'live'
                        ? 'csai'
                        : undefined,
                textTrackSettings: false,
                autoplay: this.props.play,
                disableNielsen: true,
                nielsenInstanceName: `SWM_PLAYER_${cuid()}`,
            })

            if (!this.mounted) {
                return
            }

            // player is already initialized by the bc() method, so we only use videojs to get the reference to it
            this.playerInstance = videojs.getPlayer(videoElement)

            const poster = scaledPosterSrc(
                this.props.currentVideo,
                this.props.product,
                this.props.currentVideoMetadata,
            )

            if (poster) {
                this.playerInstance.poster(poster)
            }

            this.playerInstanceEventsHandler = new EventsHandler({
                player: new RealPlayerStateAdapter(this, this.playerInstance),
                onEvent: this.props.onEvent,
                adBlockEnabled,
            })
            this.playerInstanceEventsHandler.videoChanged()

            if (this.props.playerOptions.preloadOption) {
                this.playerInstance.preload(
                    this.props.playerOptions.preloadOption,
                )
            }

            // force controller to show for all video ads SWM-4348
            this.playerInstance.on('mouseover', this.playerOnMouseOver)

            // Ensure mute state is enforced whenever video metadata is loaded or ad starts
            this.playerInstance.on('loadedmetadata', this.enforceMuteState)
            this.playerInstance.on('ima3-started', this.enforceMuteState)
            this.playerInstance.on('playing', this.enforceMuteState)

            // the ad player volume does not reset upon ad play on iOS P19-688
            if (isMobile.iOS()) {
                this.playerInstance.on('ima3-started', this.resetAdPlayerVolume)
            }
        } catch (error) {
            const err = handleUnknownError(error)
            this.setState({ errorEncountered: err })
            this.props.logger.error({ err }, 'SwmPlayer mount video error')
        }
    }

    private playerOnMouseOver = () => {
        this.playerInstance && this.playerInstance.userActive(true)
    }

    private resetAdPlayerVolume = () => {
        const player = this.playerInstance

        if (!player || !player.ima3 || !player.ima3.adPlayer) return

        player.ima3.volume = player.volume()

        if (player.muted()) {
            player.ima3.adPlayer.muted(true)
        } else {
            player.ima3.adPlayer.volume(
                player.volume() || player.ads.preAdVolume_,
            )
        }
    }

    render() {
        // We encounter an error in the interface to the swmPlayer, we throw an error in the render fn
        // so it can be caught by the error boundary above
        if (this.state.errorEncountered) {
            throw this.state.errorEncountered
        }

        if (this.props.currentVideoMetadata === undefined) {
            return null
        }

        // Get the ad application id paths for video targeting
        const unitPath = this.props.playerOptions.adUnitPath.split('/')
        const [, , ...applicationIdPaths] = unitPath

        const poster = scaledPosterSrc(
            this.props.currentVideo,
            this.props.product,
            this.props.currentVideoMetadata,
        )

        return (
            <>
                <StyledBrightcoveVideo
                    ref={this.playerInstanceRef}
                    id={this.videoElementId}
                    playsInline={true}
                    data-account={this.props.currentVideo.accountId}
                    // from old video player
                    data-application-id={
                        applicationIdPaths.length
                            ? applicationIdPaths.join('/')
                            : 'WAN'
                    }
                    data-embed="default"
                    data-player={this.props.playerOptions.brightcovePlayerId}
                    data-video-embed-id={this.props.currentVideo.id}
                    poster={poster}
                    className="video-js"
                    preload={this.props.playerOptions.preloadOption}
                    muted={this.props.isMuted}
                    controls
                    autoPlay={this.isMobileAutoPlayEnabled}
                />
            </>
        )
    }
}
